import { lazy } from 'react';
import type { DeploymentEnv } from '@/env';

const DashboardV2Page = lazy(() =>
  import('@/modules/dashboard/DashboardV2Page').then((m) => ({
    default: m.DashboardV2Page,
  })),
);

/**
 * Dashboard V2's route resolver. Until 2026-10-08 this was a real
 * environment gate (`appEnv === 'dev'`, `Navigate` away otherwise), mirroring
 * a dev-only backend mount — both gates were removed together in the same
 * release (explicit, repeated user request: "Dashboard V2 should show in
 * prod now" — confirmed via a dedicated 5-agent swarm covering auth,
 * deployment, and integration impact before either gate was touched). The
 * function now always resolves to the real page; nothing reads `appEnv`
 * anymore, but the signature is left unchanged (still takes it, still
 * returns `JSX.Element`) so `router.tsx`'s call site and this file's own
 * test suite don't need touching beyond their assertions.
 *
 * The `lazy()` wrapping stays HERE (not inlined in `router.tsx`, unlike
 * every other page) purely for the pre-existing code-splitting benefit —
 * `DashboardV2Page` loads as its own chunk regardless of which route
 * resolved to it. This is no longer load-bearing for an environment gate
 * (there isn't one anymore), just an ordinary lazy-loaded route.
 *
 * Kept as its own module (not inline in `router.tsx`, and not a component
 * itself) for the same two reasons as before: (1)
 * `react-refresh/only-export-components` forbids a component file from also
 * exporting a plain function; (2) it stays unit-testable as a pure function
 * — see `dashboardV2RouteGate.test.tsx` — without mounting the full route
 * tree.
 */
export function resolveDashboardV2Element(_appEnv: DeploymentEnv): JSX.Element {
  return <DashboardV2Page />;
}
